import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
} from "@mui/material";
import {
  Group as GroupIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AttachMoney as RevenueIcon,
  CheckCircle as ActiveIcon,
  Label as LabelIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import toast from "react-hot-toast";

const SchoolGroups = () => {
  const theme = useTheme();
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [newGroup, setNewGroup] = useState({
    name: "",
    description: "",
    color: "#4169E1",
    criteria: "",
  });

  // Mock school groups data - Replace with API call
  const schoolGroups = [
    {
      id: 1,
      name: "Premium Schools",
      description: "High-value schools with 1000+ students",
      color: "#10b981",
      criteria: "Students >= 1000, Revenue >= 50000",
      schoolCount: 45,
      totalRevenue: 2250000,
      avgRevenue: 50000,
      conversionRate: 85,
      status: "Active",
    },
    {
      id: 2,
      name: "Growing Schools",
      description: "Medium-sized schools with 500-1000 students",
      color: "#4169E1",
      criteria: "Students 500-1000, Revenue 25000-50000",
      schoolCount: 120,
      totalRevenue: 4200000,
      avgRevenue: 35000,
      conversionRate: 72,
      status: "Active",
    },
    {
      id: 3,
      name: "Small Schools",
      description: "Small schools with less than 500 students",
      color: "#f59e0b",
      criteria: "Students < 500, Revenue < 25000",
      schoolCount: 82,
      totalRevenue: 1640000,
      avgRevenue: 20000,
      conversionRate: 58,
      status: "Active",
    },
    {
      id: 4,
      name: "Enterprise Clients",
      description: "Large institutions with multiple branches",
      color: "#8b5cf6",
      criteria: "Multiple branches, Revenue >= 100000",
      schoolCount: 15,
      totalRevenue: 1500000,
      avgRevenue: 100000,
      conversionRate: 95,
      status: "Active",
    },
    {
      id: 5,
      name: "Demo Schools",
      description: "Schools currently in trial/demo phase",
      color: "#ef4444",
      criteria: "Status = Demo, Trial period active",
      schoolCount: 23,
      totalRevenue: 0,
      avgRevenue: 0,
      conversionRate: 35,
      status: "Active",
    },
    {
      id: 6,
      name: "At-Risk Schools",
      description: "Schools with low engagement or expiring soon",
      color: "#dc2626",
      criteria: "Engagement < 50%, Days to expiry < 30",
      schoolCount: 12,
      totalRevenue: 360000,
      avgRevenue: 30000,
      conversionRate: 25,
      status: "Active",
    },
  ];

  const handleCreateGroup = () => {
    if (!newGroup.name.trim()) {
      toast.error("Please enter group name");
      return;
    }

    const group = {
      id: Date.now(),
      ...newGroup,
      schoolCount: 0,
      totalRevenue: 0,
      avgRevenue: 0,
      conversionRate: 0,
      status: "Active",
    };

    toast.success("School group created successfully!");
    setGroupDialogOpen(false);
    setNewGroup({ name: "", description: "", color: "#4169E1", criteria: "" });
  };

  const totalSchools = schoolGroups.reduce((sum, group) => sum + group.schoolCount, 0);
  const totalRevenue = schoolGroups.reduce((sum, group) => sum + group.totalRevenue, 0);
  const avgConversionRate = Math.round(
    schoolGroups.reduce((sum, group) => sum + group.conversionRate, 0) / schoolGroups.length
  );

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
              <GroupIcon sx={{ color: theme.palette.primary.main }} />
              School Groups & Categories
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Categorize schools for better targeting and conversion optimization
            </Typography>
          </Box>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setGroupDialogOpen(true)}>
            Create Group
          </Button>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5, display: "block" }}>
                  Total Groups
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {schoolGroups.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5, display: "block" }}>
                  Total Schools
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                  {totalSchools}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5, display: "block" }}>
                  Total Revenue
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                  ₹{(totalRevenue / 100000).toFixed(1)}L
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5, display: "block" }}>
                  Avg Conversion Rate
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                  {avgConversionRate}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* School Groups Grid */}
        <Grid container spacing={2}>
          {schoolGroups.map((group) => (
            <Grid item xs={12} sm={6} md={4} key={group.id}>
              <Card
                sx={{
                  border: `2px solid ${group.color}`,
                  "&:hover": {
                    boxShadow: 4,
                    transform: "translateY(-4px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(group.color, 0.1),
                          color: group.color,
                          width: 40,
                          height: 40,
                        }}
                      >
                        <LabelIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "1rem" }}>
                          {group.name}
                        </Typography>
                        <Chip
                          label={group.status}
                          size="small"
                          color="success"
                          sx={{ height: "18px", fontSize: "0.65rem", mt: 0.25 }}
                        />
                      </Box>
                    </Box>
                    <IconButton size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 2, fontSize: "0.875rem" }}>
                    {group.description}
                  </Typography>
                  <Box sx={{ mb: 1.5, p: 1, bgcolor: alpha(group.color, 0.05), borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                      Criteria: {group.criteria}
                    </Typography>
                  </Box>
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: "center", p: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block" }}>
                          Schools
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: theme.palette.primary.main }}>
                          {group.schoolCount}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: "center", p: 1, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block" }}>
                          Revenue
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: theme.palette.success.main }}>
                          ₹{(group.totalRevenue / 100000).toFixed(1)}L
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                      Conversion Rate
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                      {group.conversionRate}%
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      startIcon={<SchoolIcon />}
                      onClick={() => {
                        // Navigate to schools filtered by group
                        toast.success(`Viewing schools in ${group.name}`);
                      }}
                    >
                      View Schools
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      startIcon={<TrendingUpIcon />}
                      sx={{ bgcolor: group.color }}
                    >
                      Optimize
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Create Group Dialog */}
        <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create School Group</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Group Name"
                  value={newGroup.name}
                  onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  value={newGroup.description}
                  onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Group Color"
                  type="color"
                  value={newGroup.color}
                  onChange={(e) => setNewGroup({ ...newGroup, color: e.target.value })}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Criteria (e.g., Students >= 1000, Revenue >= 50000)"
                  value={newGroup.criteria}
                  onChange={(e) => setNewGroup({ ...newGroup, criteria: e.target.value })}
                  size="small"
                  placeholder="Define criteria for automatic grouping"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateGroup}>
              Create Group
            </Button>
          </DialogActions>
        </Dialog>
      </motion.div>
    </Box>
  );
};

export default SchoolGroups;

